import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChartEmptyState } from "./ChartEmptyState";
import { ChartHost } from "./ChartHost";
import { ChartLegend } from "./ChartLegend";
import { TimeframeToolbar } from "./TimeframeToolbar";
import { pricePrecisionFor } from "./chartTheme";
import { computeSinceISO, resolveChartTz, TIMEFRAMES } from "./dateUtils";
import type { ChartTimeframe, PriceBar } from "./types";
import { useMaterialPriceQuery } from "./useMaterialPriceQuery";
import { useNewsSignalQuery } from "./useNewsSignalQuery";

export interface MaterialChartProps {
  materialId: string;
}

const TF_KEY = "chart.timeframe";
const EXT_KEY = "chart.rangeExtensionDays";
const SHOW_FINBERT_KEY = "chart.showFinbert";

function loadTimeframe(): ChartTimeframe {
  try {
    const v = window.localStorage.getItem(TF_KEY) ?? "1h";
    return (TIMEFRAMES as readonly string[]).includes(v) ? (v as ChartTimeframe) : "1h";
  } catch {
    return "1h";
  }
}

function loadExt(): number {
  try {
    const v = Number(window.localStorage.getItem(EXT_KEY) ?? "0");
    return Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
  } catch {
    return 0;
  }
}

function loadShowFinbert(): boolean {
  try {
    return window.localStorage.getItem(SHOW_FINBERT_KEY) !== "false";
  } catch {
    return true;
  }
}

export function MaterialChart({ materialId }: MaterialChartProps) {
  const { t } = useTranslation("dashboard");
  const tz = useMemo(resolveChartTz, []);
  const [timeframe, setTimeframe] = useState<ChartTimeframe>(loadTimeframe);
  const [rangeExtensionDays, setRangeExt] = useState<number>(loadExt);
  const [showFinbert, setShowFinbert] = useState<boolean>(loadShowFinbert);
  const [crosshairTime, setCrosshairTime] = useState<string | null>(null);

  const since = useMemo(
    () => computeSinceISO(timeframe, rangeExtensionDays),
    [timeframe, rangeExtensionDays],
  );

  const priceQ = useMaterialPriceQuery({
    material_id: materialId, timeframe, since,
  });
  const signalQ = useNewsSignalQuery({
    material_id: materialId, timeframe, since,
  });

  useEffect(() => { window.localStorage.setItem(TF_KEY, timeframe); }, [timeframe]);
  useEffect(() => { window.localStorage.setItem(EXT_KEY, String(rangeExtensionDays)); }, [rangeExtensionDays]);
  useEffect(() => { window.localStorage.setItem(SHOW_FINBERT_KEY, String(showFinbert)); }, [showFinbert]);

  const onCrosshairMove = useCallback((info: { t?: unknown } | null) => {
    if (!info || typeof info.t !== "number") {
      setCrosshairTime(null);
      return;
    }
    setCrosshairTime(new Date(info.t * 1000).toISOString());
  }, []);

  // Depend on the stable refetch fns — TanStack v5 returns a new result
  // object every render, which would defeat the memoization.
  const retry = useCallback(() => {
    void priceQ.refetch();
    void signalQ.refetch();
  }, [priceQ.refetch, signalQ.refetch]);

  // -------- state matrix (spec §8.8) -----------------------------------
  const isLoading = priceQ.isLoading || signalQ.isLoading;
  const isDisabled =
    signalQ.data?.material_status === "disabled" ||
    priceQ.data?.material_status === "disabled";
  const is503 = (e: unknown) => (e as { status?: number } | null)?.status === 503;
  // Degraded replaces the chart ONLY when there's nothing cached to show;
  // a 503 on a background poll over good data falls through to the stale
  // badge like any other refetch failure.
  const isDegraded =
    (priceQ.error && is503(priceQ.error) && !priceQ.data) ||
    (signalQ.error && is503(signalQ.error) && !signalQ.data);
  // A non-503 failure with nothing cached is an ERROR, not "no data" —
  // the old fall-through claimed "Waiting for MT5 EA" while the API was
  // down. With cached data we keep rendering it and show a stale badge.
  const isHardError =
    (signalQ.isError && !signalQ.data && !is503(signalQ.error)) ||
    (priceQ.isError && !priceQ.data && !is503(priceQ.error));
  const isStale =
    (signalQ.isError && !!signalQ.data) || (priceQ.isError && !!priceQ.data);

  if (isLoading) {
    return (
      <div data-testid="material-chart-loading">
        <ChartEmptyState kind="loading" />
      </div>
    );
  }
  if (isDisabled) {
    return (
      <div data-testid="material-chart-disabled">
        <ChartEmptyState kind="disabled" />
      </div>
    );
  }
  if (isDegraded) {
    return (
      <div data-testid="material-chart-degraded">
        <ChartEmptyState kind="degraded" />
      </div>
    );
  }
  if (isHardError) {
    return (
      <div data-testid="material-chart-error">
        <ChartEmptyState kind="error" onRetry={retry} />
      </div>
    );
  }

  const bars: PriceBar[] = priceQ.data?.bars ?? [];
  const l6 = signalQ.data?.l6 ?? [];
  const finbert = signalQ.data?.finbert ?? [];
  const l6SeriesStartsAt = signalQ.data?.l6_series_starts_at ?? null;
  const unmappedLabels = signalQ.data?.unmapped_labels ?? [];

  // Crosshair-pinned data points for the legend.
  // Normalize to unix seconds for comparison (API uses ISO strings without ms)
  const crosshairUnix = crosshairTime
    ? Math.floor(new Date(crosshairTime).getTime() / 1000)
    : null;
  const sameBucket = (iso: string) =>
    Math.floor(new Date(iso).getTime() / 1000) === crosshairUnix;

  const priceAt = crosshairUnix !== null
    ? bars.find((b) => sameBucket(b.t)) ?? null
    : null;
  const l6At = crosshairUnix !== null
    ? l6.find((p) => sameBucket(p.t)) ?? null
    : null;
  const finbertAt = crosshairUnix !== null
    ? finbert.find((p) => sameBucket(p.t)) ?? null
    : null;

  return (
    <div data-testid="material-chart" className="border rounded p-2 mb-2">
      <div className="flex items-center justify-between gap-2 pb-1">
        <TimeframeToolbar
          timeframe={timeframe}
          rangeExtensionDays={rangeExtensionDays}
          onTimeframeChange={setTimeframe}
          onRangeExtensionChange={setRangeExt}
        />
        <div className="flex items-center gap-2">
          {isStale && (
            <span
              data-testid="chart-stale-badge"
              className="px-2 py-0.5 text-xs rounded bg-destructive/15 text-destructive"
              title={t("chart.stale")}
            >
              {t("chart.stale")}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowFinbert((v) => !v)}
            aria-pressed={showFinbert}
            className="px-2 py-1 text-xs rounded border"
          >
            {t("chart.finbertToggle", {
              state: showFinbert ? t("chart.on") : t("chart.off"),
            })}
          </button>
        </div>
      </div>
      <ChartLegend
        tz={tz}
        crosshairTime={crosshairTime}
        price={priceAt}
        l6={l6At}
        finbert={finbertAt}
        pricePrecision={pricePrecisionFor(materialId)}
      />
      <ChartHost
        timeframe={timeframe}
        tz={tz}
        bars={bars}
        l6={l6}
        finbert={finbert}
        onCrosshairMove={onCrosshairMove}
        showFinbert={showFinbert}
        pricePrecision={pricePrecisionFor(materialId)}
      />
      {bars.length === 0 && !priceQ.isError && <ChartEmptyState kind="no-price" />}
      {l6.length === 0 && !signalQ.isError && <ChartEmptyState kind="no-l6" />}
      {l6.length > 0 && l6SeriesStartsAt &&
        Date.parse(l6SeriesStartsAt) > Date.parse(since) && (
          <ChartEmptyState kind="l6-cold-start" l6SeriesStartsAt={l6SeriesStartsAt} />
        )}
      {finbert.length === 0 && <ChartEmptyState kind="no-finbert" />}
      {unmappedLabels.length > 0 && (
        <ChartEmptyState kind="unmapped-labels" unmappedLabels={unmappedLabels} />
      )}
    </div>
  );
}
